
import { createContext, useContext, useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";

export type Notification = {
  id: string;
  title: string;
  message: string;
  type: "itinerary_request" | "system";
  source_id: string;
  is_read: boolean;
  created_at: string;
};

type NotificationsContextType = {
  notifications: Notification[];
  unreadCount: number;
  markAsRead: (id: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  refreshNotifications: () => void;
  loading: boolean;
};

const NotificationsContext = createContext<NotificationsContextType | undefined>(undefined);

export function NotificationsProvider({ children }: { children: React.ReactNode }) {
  const [forceRefresh, setForceRefresh] = useState(0);
  
  const { data: notifications = [], isLoading: loading } = useQuery({
    queryKey: ["notifications", forceRefresh],
    queryFn: async () => {
      // For now, we'll use itinerary requests with status "new" as our notifications
      const { data, error } = await supabase
        .from("itinerary_requests")
        .select("*")
        .eq("status", "new")
        .order("created_at", { ascending: false });
      
      if (error) {
        console.error("Error fetching notifications:", error);
        throw new Error(error.message);
      }
      
      return data.map(request => ({
        id: request.id,
        title: "New Itinerary Request",
        message: `${request.name} requested a ${request.destination} itinerary`,
        type: "itinerary_request" as const,
        source_id: request.id,
        is_read: false,
        created_at: request.created_at
      }));
    },
    enabled: true,
  });
  
  const unreadCount = notifications.filter(n => !n.is_read).length;
  
  const refreshNotifications = () => {
    setForceRefresh(prev => prev + 1);
  };
  
  const markAsRead = async (id: string) => {
    // In a real application, you would update a notifications table
    // For now, we'll update the itinerary request status
    const { error } = await supabase
      .from("itinerary_requests")
      .update({ status: "in-progress" })
      .eq("id", id);
    
    if (error) {
      console.error("Error marking notification as read:", error);
      throw new Error(error.message);
    }
    
    refreshNotifications();
  };
  
  const markAllAsRead = async () => {
    // In a real application, you would update all notifications for the current user
    // For now, we'll update all new itinerary requests
    const { error } = await supabase
      .from("itinerary_requests")
      .update({ status: "in-progress" })
      .eq("status", "new");
    
    if (error) {
      console.error("Error marking all notifications as read:", error);
      throw new Error(error.message);
    }
    
    refreshNotifications();
  };
  
  // Refresh notifications every minute to catch new requests
  useEffect(() => {
    const interval = setInterval(() => {
      refreshNotifications();
    }, 60000);
    
    return () => clearInterval(interval);
  }, []);
  
  return (
    <NotificationsContext.Provider
      value={{
        notifications,
        unreadCount,
        markAsRead,
        markAllAsRead,
        refreshNotifications,
        loading
      }}
    >
      {children}
    </NotificationsContext.Provider>
  );
}

export function useNotifications() {
  const context = useContext(NotificationsContext);
  
  if (context === undefined) {
    throw new Error("useNotifications must be used within a NotificationsProvider");
  }
  
  return context;
}
